/**
 * Ranking System - Manages player scores and enemy names
 */

export class RankingSystem {
    constructor(game) {
        this.game = game;

        // Player stats
        this.playerScore = 0;
        this.playerName = "Player"; // Player name in different color
        this.enemiesDestroyed = 0;
        this.timeAlive = 0;
        this.deaths = 0;

        // Random enemy names pool - 简短易区分的代号
        this.enemyNames = [
            "adjvdgt", "huahua", "kzxjw", "lmnoqp", "rstuv", "wxyzab",
            "ghost", "storm", "hawk", "wolf", "tiger", "dragon",
            "shark", "eagle", "bear", "lion", "snake", "spider",
            "raven", "viper", "cobra", "falcon", "panda", "rhino",
            "thunder", "shadow", "flame", "frost", "wind", "ocean",
            "steel", "iron"
        ];

        // Fixed enemy slots system - each name is a fixed slot with persistent gold
        this.enemySlots = new Map(); // { 'Captain Redbeard': 150, 'Blackwater Jake': 200, ... }
        this.nextEnemyNameIndex = 0; // Index for sequential name assignment

        // Track used names to avoid duplicates (kept for backwards compatibility)
        this.usedNames = new Set();

        // Leaderboard (simulated other players)
        this.leaderboard = [
            { name: "Admiral Storm", score: 1250, color: 0xFFD700 },
            { name: "Captain Thunder", score: 980, color: 0xC0C0C0 },
            { name: "Sea Master", score: 750, color: 0xCD7F32 },
            { name: "Wave Hunter", score: 650, color: 0x888888 },
            { name: "Iron Sailor", score: 520, color: 0x888888 }
        ];

        // Player position in leaderboard
        this.playerRank = this.leaderboard.length + 1;
    }

    // Get next enemy name (sequential assignment for fixed slots)
    getNextEnemyName() {
        // Assign names sequentially from the pool
        const name = this.enemyNames[this.nextEnemyNameIndex % this.enemyNames.length];
        this.nextEnemyNameIndex++;

        // Initialize slot if it doesn't exist
        if (!this.enemySlots.has(name)) {
            this.enemySlots.set(name, 0);
        }

        return name;
    }

    // Legacy method for backwards compatibility - now calls getNextEnemyName
    getRandomEnemyName() {
        return this.getNextEnemyName();
    }

    // Save enemy gold to persistent slot
    saveEnemyGold(name, gold) {
        if (!name) return;
        this.enemySlots.set(name, Math.max(0, gold || 0));
        console.log(`💰 Saved ${gold} gold for ${name}`);
    }

    // Get enemy gold from persistent slot
    getEnemyGold(name) {
        if (!name) return 0;
        return this.enemySlots.get(name) || 0;
    }

    // Add score for destroying an enemy
    addEnemyKill() {
        this.enemiesDestroyed++;
        // 不再在这里管理分数，直接从PlayerShip获取
        console.log(`Enemy killed. Total kills: ${this.enemiesDestroyed}`); // 调试信息
        this.updatePlayerRank();
    }

    resetKillCount() {
        this.enemiesDestroyed = 0;
        this.updatePlayerRank();
    }

    // Get player kill count
    getPlayerKills() {
        return this.enemiesDestroyed;
    }

    // Player died
    recordPlayerDeath() {
        this.deaths++;
        this.timeAlive = 0; // Reset survival time

    }

    // Update survival time
    updateSurvivalTime(deltaTime) {
        this.timeAlive += deltaTime;
    }

    // Update player rank based on score
    updatePlayerRank() {
        const currentScore = this.getPlayerScore();
        let rank = 1;
        for (let entry of this.leaderboard) {
            if (currentScore <= entry.score) {
                rank++;
            }
        }
        this.playerRank = rank;
    }

    // 获取玩家当前分数
    getPlayerScore() {
        if (this.game && this.game.player) {
            return this.game.player.score || 0;
        }
        return this.playerScore; // 备用分数
    }

    // Get current leaderboard with player inserted
    getCurrentLeaderboard() {
        const playerEntry = {
            name: this.playerName,
            score: this.getPlayerScore(),
            color: 0x00FF00, // Green for player
            isPlayer: true
        };

        // Insert player into leaderboard
        const fullLeaderboard = [...this.leaderboard, playerEntry];
        fullLeaderboard.sort((a, b) => b.score - a.score);

        return fullLeaderboard.slice(0, 10); // Top 10
    }

    // Get all ships (player + all enemy slots) ranked by gold coins
    getAllShipsByGold() {
        if (!this.game) return [];

        const ships = [];

        // Add player ship（显示总金币，不是实时金币）
        if (this.game.player) {
            ships.push({
                name: this.playerName,
                gold: this.game.coinSystem?.getTotalEarnedCoins?.() || 0,
                isPlayer: true
            });
        }

        // Add ALL enemy slots (including dead enemies)
        this.enemySlots.forEach((gold, name) => {
            ships.push({
                name: name,
                gold: gold,
                isPlayer: false
            });
        });

        // Sort by gold (descending)
        ships.sort((a, b) => b.gold - a.gold);

        return ships;
    }

    // Get player statistics
    getPlayerStats() {
        // Get player level from the game player object if available
        let playerLevel = 1;
        if (this.game && this.game.player) {
            playerLevel = this.game.player.level || 1;
        }
        
        return {
            name: this.playerName,
            score: this.getPlayerScore(),
            level: playerLevel,
            rank: this.playerRank,
            enemiesDestroyed: this.enemiesDestroyed,
            timeAlive: this.timeAlive,
            deaths: this.deaths,
            killDeathRatio: this.deaths > 0 ? (this.enemiesDestroyed / this.deaths).toFixed(2) : this.enemiesDestroyed
        };
    }

    // Display ranking information
    displayRanking() {
        const stats = this.getPlayerStats();
        const leaderboard = this.getCurrentLeaderboard();

    }

    // Reset player stats (for new game)
    resetPlayerStats() {
        this.enemiesDestroyed = 0;
        this.timeAlive = 0;
        this.deaths = 0;
        this.updatePlayerRank();

        // 重置玩家分数
        if (this.game && this.game.player) {
            this.game.player.score = 0;
        }
    }
}
